import { useState, useRef, useEffect, useCallback } from "react";
import { Box, ExternalLink, Sparkles } from "lucide-react";
import { apiClient } from "../api/client";
import type { RazorpayOptions } from "../api/payments";
import { analyzeMesh } from "../api/meshRepair";
import { sendToQuote } from "../api/handoff";
import { useAuth } from "../api/auth";
import SellAskCard from "./SellAskCard";
import FofyMascot from "./FofyMascot";

// ─── Types ───────────────────────────────────────────────────────────────────

type MessageType =
  | "text"
  | "image"
  | "model_generating"
  | "model_preview"
  | "printability_result"
  | "quote_result"
  | "clarify_question"
  | "order_status"
  | "payment_card"
  | "product_card"
  | "cost_estimate"
  | "order"
  | "error";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  message_type: MessageType;
  suggestions?: string[];
  attachments?: Record<string, unknown>;
}

interface GenStatus {
  status: string;
  preview_url?: string;
  model_ref?: string;
  download_price?: number;
  message?: string;
}

// ─── Razorpay Checkout.js loader ─────────────────────────────────────────────

let rzpLoaded = false;
function loadRazorpay(): Promise<void> {
  if (rzpLoaded && window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => {
      rzpLoaded = true;
      resolve();
    };
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.head.appendChild(s);
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FofyChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "init",
      role: "assistant",
      content: "Hi! I'm Fofy. Upload a photo, describe what you want to make, or upload a 3D file (STL/OBJ/3MF) — I'll generate a 3D model, find a designer, or print it for you. All right here.",
      message_type: "text",
      suggestions: ["📷 Upload a photo", "📦 Upload a 3D file", "I want a phone holder", "How much does 3D printing cost?"],
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInput3dRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pendingClarify, setPendingClarify] = useState<{
    prompt: string;
    step: number;
    answers: Record<string, string>;
  } | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ─── Session management ─────────────────────────────────────────────────────

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    try {
      const res = await apiClient.post("/ai/sessions", { title: "Fofy Chat", site: "designai" });
      const sid = res.data.id;
      setSessionId(sid);
      return sid;
    } catch {
      // Fallback to stateless /ai/help
      return "";
    }
  }, [sessionId]);

  // ─── Add message helper ─────────────────────────────────────────────────────

  const addMessage = useCallback((msg: Omit<ChatMessage, "id">) => {
    setMessages((m) => [...m, { ...msg, id: `${Date.now()}-${Math.random()}` }]);
  }, []);
  // LAW 3 (swarm 23 Oct): Fofy = the trust bridge — AI-generation handoffs land
  // here. Any surface (Create3D, explore rescue-card) can dispatch:
  //   window.dispatchEvent(new CustomEvent("fofy:handoff", { detail: {...} }))
  // and Fofy converts it into an actionable card in the chat.
  useEffect(() => {
    const onHandoff = (e: Event) => {
      try {
        const d = (e as CustomEvent).detail || {};
        const title = d.title || "Your 3D model is ready";
        const modelUrl = d.model_url || "";
        const price = d.price !== undefined ? `₹${d.price}` : null;
        const lines: string[] = [title];
        if (price) lines.push(`Slice-verified print & delivery: ${price} (₹2.50/g PLA, 3–5 days across India)`);
        if (modelUrl) lines.push(`Model file: ${modelUrl}`);
        addMessage({
          role: "assistant",
          content: lines.join("\n"),
          message_type: d.price ? "payment_card" : "quote_result",
          attachments: {
            model_url: modelUrl,
            price: d.price ?? null,
            provider: d.provider || null,
            cta: d.price ? "Print & deliver this" : "Get a print quote",
          },
        } as never);
      } catch {
        /* worst case: handoff never appears — never break the chat */
      }
    };
    window.addEventListener("fofy:handoff", onHandoff);
    return () => window.removeEventListener("fofy:handoff", onHandoff);
  }, [addMessage]);

  // ─── 3D generation polling ──────────────────────────────────────────────────

  const pollGenStatus = useCallback(async (sid: string, taskId: string, provider: string, isImage: boolean) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes at 5s intervals

    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (pollRef.current) clearInterval(pollRef.current);
        addMessage({
          role: "assistant",
          content: "Generation timed out. Please try again.",
          message_type: "error",
        });
        return;
      }

      try {
        const res = await apiClient.get<GenStatus>(
          `/ai/sessions/${sid}/gen-status/${taskId}?provider=${provider}&is_image=${isImage}`,
        );
        const data = res.data;

        if (data.status === "SUCCEEDED" || data.status === "SUCCESS") {
          if (pollRef.current) clearInterval(pollRef.current);
          // The backend already created a model_preview message in the DB.
          // We add it to the UI directly for instant feedback.
          addMessage({
            role: "assistant",
            content: data.message || "Your 3D model is ready!",
            message_type: "model_preview",
            attachments: {
              preview_url: data.preview_url,
              model_ref: data.model_ref,
              download_price: data.download_price || 10000,
              actions: ["download_stl", "print", "refine_designer"],
            },
          });
        } else if (data.status === "FAILED") {
          if (pollRef.current) clearInterval(pollRef.current);
          addMessage({
            role: "assistant",
            content: `Generation failed: ${data.message || "Unknown error"}`,
            message_type: "error",
          });
        }
        // PENDING → keep polling
      } catch {
        // Network error → keep polling, don't spam user
      }
    }, 5000);
  }, [addMessage]);

  // ─── CLARIFY stage ──────────────────────────────────────────────────────────
  // Ask dimensions + use case before burning a credit on generation.

  const clarifyQuestions = [
    {
      key: "size",
      question: "Got it — I can make that for you! Quick question: roughly what size do you need? (e.g. \"5 cm\", \"phone-sized\", \"fits on a desk\")",
      chips: ["Small (under 5 cm)", "Medium (5–15 cm)", "Large (over 15 cm)", "Not sure"],
    },
    {
      key: "use",
      question: "And where will you use it? This helps me design the right version.",
      chips: ["Desk / shelf", "Wall mount", "Outdoor", "Functional / mechanical", "Decorative"],
    },
  ];

  const startClarify = (prompt: string, _sid: string) => {
    setPendingClarify({ prompt, step: 0, answers: {} });
    const q = clarifyQuestions[0];
    addMessage({
      role: "assistant",
      content: q.question,
      message_type: "clarify_question",
      suggestions: q.chips,
      attachments: { clarify_step: 0, clarify_key: q.key },
    });
  };

  const answerClarify = (answer: string) => {
    if (!pendingClarify) return;
    const step = pendingClarify.step;
    const key = clarifyQuestions[step].key;
    const answers = { ...pendingClarify.answers, [key]: answer };
    addMessage({ role: "user", content: answer, message_type: "text" });

    const nextStep = step + 1;
    if (nextStep < clarifyQuestions.length) {
      setPendingClarify({ ...pendingClarify, step: nextStep, answers });
      const q = clarifyQuestions[nextStep];
      addMessage({
        role: "assistant",
        content: q.question,
        message_type: "clarify_question",
        suggestions: q.chips,
        attachments: { clarify_step: nextStep, clarify_key: q.key },
      });
    } else {
      // All questions answered — build enhanced prompt and generate
      const enhanced = buildEnhancedPrompt(pendingClarify.prompt, answers);
      setPendingClarify(null);
      generateAfterClarify(enhanced);
    }
  };

  const buildEnhancedPrompt = (original: string, answers: Record<string, string>): string => {
    const size = answers["size"] || "";
    const use = answers["use"] || "";
    let prompt = original;
    if (size && size !== "Not sure") prompt += `, size: ${size}`;
    if (use) prompt += `, use case: ${use}`;
    return prompt;
  };

  const generateAfterClarify = async (prompt: string) => {
    setLoading(true);
    try {
      const sid = await ensureSession();
      if (!sid) throw new Error("No session");

      addMessage({
        role: "assistant",
        content: `Generating: "${prompt}" — this takes 30-60s.`,
        message_type: "text",
      });

      const genRes = await apiClient.post(`/ai/sessions/${sid}/generate`, {
        prompt,
        provider: "tripo3d",
      });
      const genData = genRes.data;

      if (genData.status === "insufficient_credits") {
        addMessage({
          role: "assistant",
          content: `You need 1 coin to generate. You have ${genData.balance}. Pick a pack:`,
          message_type: "product_card",
          attachments: { card_type: "credit_packs", packs: genData.packs },
        });
      } else if (genData.status === "generating") {
        addMessage({
          role: "assistant",
          content: "Generating 3D model... This takes 30-60s.",
          message_type: "model_generating",
          attachments: { model_ref: genData.model_ref, provider: genData.provider },
        });
        pollGenStatus(sid, genData.task_id, genData.provider, false);
      }
    } catch {
      addMessage({
        role: "assistant",
        content: "Generation failed. Try again or contact FOFUS at +91-98958-54640.",
        message_type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── ORDER STATUS — track handoff/quote status ──────────────────────────────

  const checkOrderStatus = async (modelRef: string) => {
    setLoading(true);
    try {
      const { getHandoffStatus } = await import("../api/handoff");
      const status = await getHandoffStatus(modelRef);

      const statusLabels: Record<string, string> = {
        pending: "Preparing your quote...",
        succeeded: "Quote ready!",
        failed: "Failed — will retry automatically",
        retrying: "Retrying...",
      };

      const label = statusLabels[status.status] || status.status;
      let content = `Order status: ${label}`;
      if (status.quote_url && status.status === "succeeded") {
        content += `\n\nYour quote is ready — click to view and place your order.`;
      }
      if (status.status === "failed" && status.last_error) {
        content += `\n\nLast issue: ${status.last_error}`;
      }
      if (status.attempts > 0 && status.status !== "succeeded") {
        content += `\n\nAttempt ${status.attempts}/${status.max_attempts}`;
      }

      addMessage({
        role: "assistant",
        content,
        message_type: "order_status",
        attachments: {
          status: status.status,
          quote_url: status.quote_url,
          model_ref: modelRef,
        },
      });
    } catch {
      addMessage({
        role: "assistant",
        content: "Couldn't find order status for that model. If you just placed it, try again in a minute.",
        message_type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Send text message ──────────────────────────────────────────────────────

  const send = async (text: string) => {
    // Intercept the "Upload a photo" suggestion chip
    if (text.toLowerCase().includes("upload a photo") || text.toLowerCase().includes("📷")) {
      fileInputRef.current?.click();
      return;
    }
    // Intercept the "Upload a 3D file" suggestion chip
    if (text.toLowerCase().includes("upload a 3d file") || text.toLowerCase().includes("📦")) {
      fileInput3dRef.current?.click();
      return;
    }
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setInput("");
    addMessage({ role: "user", content: userMsg, message_type: "text" });
    setLoading(true);

    try {
      const sid = await ensureSession();

      // Check if user wants to generate something
      const genKeywords = ["generate", "make", "create", "3d", "model", "print", "design", "stl"];
      const wantsGen = genKeywords.some((k) => userMsg.toLowerCase().includes(k));

      if (sid && wantsGen) {
        // ─── CLARIFY stage — ask before generating ───────────────────────
        // Don't burn a credit on a vague prompt. Ask 2 quick questions.
        startClarify(userMsg, sid);
        return;
      } else {
        // Regular chat
        const res = await apiClient.post("/ai/help", { message: userMsg, session_id: sid || undefined });
        addMessage({
          role: "assistant",
          content: res.data.reply,
          message_type: (res.data.message_type as MessageType) || "text",
          suggestions: res.data.suggestions,
          attachments: res.data.attachments,
        });
      }
    } catch {
      addMessage({
        role: "assistant",
        content: "Sorry, something went wrong. Try again or contact FOFUS at +91-98958-54640.",
        message_type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Photo upload ───────────────────────────────────────────────────────────

  const handlePhotoUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      addMessage({ role: "assistant", content: "Image too large (max 10 MB).", message_type: "error" });
      return;
    }

    setLoading(true);
    const fileUrl = URL.createObjectURL(file);
    addMessage({
      role: "user",
      content: `Uploaded photo: ${file.name}`,
      message_type: "image",
      attachments: { filename: file.name, file_url: fileUrl },
    });

    try {
      const sid = await ensureSession();
      if (!sid) throw new Error("No session");

      const formData = new FormData();
      formData.append("image", file);

      const res = await apiClient.post(`/ai/sessions/${sid}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { provider: "tripo3d" },
      });
      const data = res.data;

      if (data.status === "insufficient_credits") {
        addMessage({
          role: "assistant",
          content: `You need 1 coin to generate. You have ${data.balance}. Pick a pack:`,
          message_type: "product_card",
          attachments: { card_type: "credit_packs", packs: data.packs },
        });
      } else if (data.status === "generating") {
        addMessage({
          role: "assistant",
          content: "Generating 3D model from your photo... This takes 30-60s.",
          message_type: "model_generating",
          attachments: { model_ref: data.model_ref, provider: data.provider, is_image: true },
        });
        pollGenStatus(sid, data.task_id, data.provider, true);
      }
    } catch {
      addMessage({
        role: "assistant",
        content: "Couldn't process your photo. Try a clearer image (PNG, JPG, or WebP).",
        message_type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── 3D file upload (STL/OBJ/3MF) ────────────────────────────────────────────

  const handle3dUpload = async (file: File) => {
    const ext = file.name.toLowerCase().split(".").pop() || "";
    if (!["stl", "obj", "3mf"].includes(ext)) {
      addMessage({ role: "assistant", content: "Unsupported file type. Upload STL, OBJ, or 3MF files.", message_type: "error" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      addMessage({ role: "assistant", content: "File too large (max 50 MB).", message_type: "error" });
      return;
    }

    setLoading(true);
    addMessage({
      role: "user",
      content: `Uploaded 3D file: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`,
      message_type: "text",
    });

    try {
      const sid = await ensureSession();
      if (!sid) throw new Error("No session");

      const formData = new FormData();
      formData.append("file", file);

      const res = await apiClient.post(`/ai/sessions/${sid}/upload-3d`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data;

      addMessage({
        role: "assistant",
        content: `✓ Analyzed: ${data.filename}\n\n` +
          `Dimensions: ${data.dimensions_mm[0]} × ${data.dimensions_mm[1]} × ${data.dimensions_mm[2]} mm\n` +
          `Volume: ${data.volume_cm3} cm³  |  Weight: ~${data.weight_g}g\n` +
          `Faces: ${data.face_count.toLocaleString()}  |  Watertight: ${data.is_watertight ? "Yes" : "No"}\n` +
          `Issues: ${data.issues.length > 0 ? data.issues.join(", ") : "None — print ready!"}\n` +
          `Est. cost: ₹${data.est_cost} (standard PLA)\n\n` +
          `What would you like to do?`,
        message_type: "product_card",
        attachments: {
          card_type: "uploaded_3d",
          model_ref: data.model_ref,
          filename: data.filename,
          dimensions_mm: data.dimensions_mm,
          volume_cm3: data.volume_cm3,
          weight_g: data.weight_g,
          face_count: data.face_count,
          is_watertight: data.is_watertight,
          issues: data.issues,
          est_cost: data.est_cost,
        },
      });
    } catch (err: unknown) {
      const e = err as { detail?: string };
      addMessage({
        role: "assistant",
        content: e?.detail || "Couldn't analyze your 3D file. Make sure it's a valid STL, OBJ, or 3MF file.",
        message_type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Razorpay payment ───────────────────────────────────────────────────────

  const startPayment = async (
    paymentType: "download" | "credits" | "manufacturing",
    modelRef?: string,
    creditPackId?: number,
    amount?: number,
  ) => {
    try {
      const sid = sessionId;
      if (!sid) return;

      const res = await apiClient.post(`/ai/sessions/${sid}/pay`, {
        payment_type: paymentType,
        model_ref: modelRef,
        credit_pack_id: creditPackId,
        amount,
      });
      const { order_id, amount: payAmount, key_id, description } = res.data;

      await loadRazorpay();

      const options: RazorpayOptions = {
        key: key_id,
        amount: payAmount,
        currency: "INR",
        name: "FOFUS",
        description,
        order_id,
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          // Verify payment
          try {
            const verifyRes = await apiClient.post(`/ai/sessions/${sid}/pay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_type: paymentType,
              model_ref: modelRef,
              credit_pack_id: creditPackId,
            });
            const vData = verifyRes.data;

            if (paymentType === "download" && vData.download_unlocked) {
              addMessage({
                role: "assistant",
                content: "Payment verified! Your STL download is unlocked.",
                message_type: "text",
                attachments: { model_ref: modelRef, download_unlocked: true },
              });
            } else if (paymentType === "credits" && vData.credits_added > 0) {
              addMessage({
                role: "assistant",
                content: `Payment verified! ${vData.credits_added} coins added. What would you like to generate?`,
                message_type: "text",
              });
            } else {
              addMessage({
                role: "assistant",
                content: "Payment verified!",
                message_type: "text",
              });
            }
          } catch {
            addMessage({
              role: "assistant",
              content: "Payment verification failed. Contact support@fofus.in if you were charged.",
              message_type: "error",
            });
          }
        },
        prefill: {
          name: "FOFUS Customer",
        },
        theme: { color: "#3b82f6" },
        modal: {
          ondismiss: () => {
            addMessage({
              role: "assistant",
              content: "Payment cancelled. You can try again anytime.",
              message_type: "text",
            });
          },
        },
      };

      if (!window.Razorpay) throw new Error("Razorpay not loaded");
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      addMessage({
        role: "assistant",
        content: "Couldn't start payment. Please try again.",
        message_type: "error",
      });
    }
  };

  // ─── Manufacturing flow ─────────────────────────────────────────────────────

  const startManufacture = async (modelRef: string) => {
    try {
      const sid = sessionId;
      if (!sid) return;
      const res = await apiClient.post(`/ai/sessions/${sid}/manufacture`, { model_ref: modelRef });
      addMessage({
        role: "assistant",
        content: "Pick a material for your print:",
        message_type: "product_card",
        attachments: res.data,
      });
    } catch {
      addMessage({ role: "assistant", content: "Couldn't load materials. Try again.", message_type: "error" });
    }
  };

  const createMfgOrder = async (modelRef: string, materialId: string, quality: string, volumeCm3: number) => {
    try {
      const sid = sessionId;
      if (!sid) return;
      const res = await apiClient.post(`/ai/sessions/${sid}/manufacture/order`, {
        model_ref: modelRef,
        material_id: materialId,
        quality,
        volume_cm3: volumeCm3,
      });
      addMessage({
        role: "assistant",
        content: `Order placed! ${res.data.order_number} — ₹${res.data.selling_price}`,
        message_type: "order",
        attachments: { order_id: res.data.order_id, order_number: res.data.order_number, total_paise: res.data.total_paise },
      });
      // Auto-trigger payment for manufacturing
      startPayment("manufacturing", modelRef, undefined, res.data.total_paise);
    } catch {
      addMessage({ role: "assistant", content: "Couldn't create order. Try again.", message_type: "error" });
    }
  };

  // ─── Designer assignment ────────────────────────────────────────────────────

  const assignDesigner = async (modelRef: string) => {
    try {
      const sid = sessionId;
      if (!sid) return;
      const res = await apiClient.post(`/ai/sessions/${sid}/assign-designer`, {
        model_ref: modelRef,
        notes: "Refine model from chat",
      });
      const designers = res.data.designers || [];
      if (designers.length === 0) {
        addMessage({
          role: "assistant",
          content: "No designers available right now. Your model is saved — we'll notify you.",
          message_type: "text",
          attachments: { project_id: res.data.project_id },
        });
      } else {
        addMessage({
          role: "assistant",
          content: `Found ${designers.length} designers. Tap to hire:`,
          message_type: "product_card",
          attachments: {
            card_type: "designers",
            project_id: res.data.project_id,
            model_ref: modelRef,
            designers,
          },
        });
      }
    } catch {
      addMessage({ role: "assistant", content: "Couldn't find designers. Try again.", message_type: "error" });
    }
  };

  // ─── Check Printability (P4 bridge) ────────────────────────────────────────

  const checkPrintability = async (modelRef: string) => {
    // model_ref format: "provider:task_id"
    const parts = modelRef.split(":");
    if (parts.length < 2) {
      addMessage({ role: "assistant", content: "Can't analyze this model — missing reference.", message_type: "error" });
      return;
    }
    const provider = parts[0];
    const taskId = parts.slice(1).join(":");

    addMessage({ role: "assistant", content: "Checking printability...", message_type: "model_generating" });

    try {
      const result = await analyzeMesh(provider, taskId);
      if (result.status === "PASS") {
        addMessage({
          role: "assistant",
          content: "✓ Print-ready — no issues found! Safe to print.",
          message_type: "printability_result",
          attachments: { status: "PASS", issues: [], model_ref: modelRef },
        });
      } else if (result.status === "ISSUES") {
        addMessage({
          role: "assistant",
          content: `⚠ ${result.issues_before.length} issue(s) found:`,
          message_type: "printability_result",
          attachments: { status: "ISSUES", issues: result.issues_before, model_ref: modelRef },
        });
      } else {
        addMessage({
          role: "assistant",
          content: result.message || "Analysis failed. Try again later.",
          message_type: "error",
        });
      }
    } catch (err: unknown) {
      const e = err as { detail?: string };
      addMessage({
        role: "assistant",
        content: e?.detail || "Couldn't check printability. The model may still be processing.",
        message_type: "error",
      });
    }
  };

  // ─── Get Print Quote (P6 bridge) ─────────────────────────────────────────────

  const getPrintQuote = async (modelRef: string) => {
    const parts = modelRef.split(":");
    if (parts.length < 2) {
      addMessage({ role: "assistant", content: "Can't quote this model — missing reference.", message_type: "error" });
      return;
    }
    const provider = parts[0];
    const taskId = parts.slice(1).join(":");

    addMessage({ role: "assistant", content: "Getting print quote...", message_type: "model_generating" });

    try {
      const resp = await sendToQuote({
        provider,
        task_id: taskId,
        contact_name: undefined,
        contact_email: undefined,
        contact_phone: undefined,
        // Same ref the SellAskCard below uses — shadow-project dedupe key.
        designai_ref: user?.id ? `FOF-AI-${taskId}`.slice(0, 20) : undefined,
      });

      if (resp.queued) {
        addMessage({
          role: "assistant",
          content: "Working on your quote — we'll notify you when it's ready!",
          message_type: "quote_result",
          attachments: { queued: true, model_ref: modelRef },
        });
      } else if (resp.quote_url) {
        addMessage({
          role: "assistant",
          content: "Your print quote is ready!",
          message_type: "quote_result",
          attachments: { queued: false, quote_url: resp.quote_url, model_ref: modelRef },
        });
        window.open(resp.quote_url, "_blank");
      } else {
        addMessage({
          role: "assistant",
          content: "Quote engine unavailable right now. Try again later.",
          message_type: "error",
        });
      }
    } catch (err: unknown) {
      const e = err as { detail?: string };
      addMessage({
        role: "assistant",
        content: e?.detail || "Couldn't get a quote. The model may still be processing.",
        message_type: "error",
      });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Collapsed FAB
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        aria-label="Open Fofy Chat"
      >
        <FofyMascot state="idle" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:w-[400px] sm:rounded-2xl sm:shadow-2xl sm:border sm:border-gray-200 sm:overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 shrink-0">
        <div className="flex items-center gap-2">
          <FofyMascot state={loading ? "thinking" : "idle"} />
          <div>
            <div className="text-white font-semibold text-sm">Fofy</div>
            <div className="text-white/80 text-xs">AI 3D Design Assistant</div>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-white/80 hover:text-white text-xl leading-none"
          aria-label="Close chat"
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg) => (
          <MessageRenderer
            key={msg.id}
            msg={msg}
            fofyUser={user}
            onSuggestion={msg.message_type === "clarify_question" ? answerClarify : send}
            onDownload={(modelRef, _price) => startPayment("download", modelRef)}
            onPrint={(modelRef) => startManufacture(modelRef)}
            onRefine={(modelRef) => assignDesigner(modelRef)}
            onCheckPrintability={checkPrintability}
            onGetQuote={getPrintQuote}
            onTrackOrder={checkOrderStatus}
            onBuyCredits={(packId) => startPayment("credits", undefined, packId)}
            onManufactureOrder={createMfgOrder}
            onHireDesigner={(designerId, modelRef) => {
              addMessage({
                role: "assistant",
                content: `Designer hire request sent for ${designerId}. They'll respond within 24h.`,
                message_type: "text",
                attachments: { designer_id: designerId, model_ref: modelRef },
              });
            }}
          />
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-gray-200 p-3 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhotoUpload(file);
              e.target.value = "";
            }}
          />
          <input
            ref={fileInput3dRef}
            type="file"
            accept=".stl,.obj,.3mf,model/stl,model/obj,application/vnd.ms-3dmf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handle3dUpload(file);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 disabled:opacity-50 transition-colors shrink-0"
            aria-label="Upload photo"
            title="Upload photo"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.93 4h6.14a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <button
            onClick={() => fileInput3dRef.current?.click()}
            disabled={loading}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 border border-green-200 text-green-600 hover:bg-green-100 disabled:opacity-50 transition-colors shrink-0"
            aria-label="Upload 3D file"
            title="Upload STL / OBJ / 3MF"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12" />
            </svg>
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send(input)}
            placeholder="Describe or upload a photo..."
            className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
            disabled={loading}
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            className="rounded-full bg-blue-500 px-4 py-2 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors shrink-0"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Message Renderer ────────────────────────────────────────────────────────

interface MessageRendererProps {
  msg: ChatMessage;
  fofyUser: ReturnType<typeof useAuth>["user"];
  onSuggestion: (text: string) => void;
  onDownload: (modelRef: string, price: number) => void;
  onPrint: (modelRef: string) => void;
  onRefine: (modelRef: string) => void;
  onCheckPrintability: (modelRef: string) => void;
  onGetQuote: (modelRef: string) => void;
  onTrackOrder: (modelRef: string) => void;
  onBuyCredits: (packId: number) => void;
  onManufactureOrder: (modelRef: string, materialId: string, quality: string, volumeCm3: number) => void;
  onHireDesigner: (designerId: string, modelRef: string) => void;
}

function MessageRenderer(props: MessageRendererProps) {
  const { msg, onSuggestion, fofyUser } = props;
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
          isUser
            ? "bg-blue-500 text-white rounded-br-sm"
            : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
        }`}
      >
        {/* Text message */}
        {(msg.message_type === "text" || msg.message_type === "error") && (
          <>
            <p className={msg.message_type === "error" ? "text-red-600 font-medium" : "whitespace-pre-wrap"}>
              {msg.content}
            </p>
            {msg.suggestions && msg.suggestions.length > 0 && (
              <div className="mt-2 space-y-1">
                {msg.suggestions.map((s, j) => (
                  <button
                    key={j}
                    onClick={() => onSuggestion(s)}
                    className="block w-full text-left text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg px-2 py-1 transition-colors"
                  >
                    → {s}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Image upload — show actual photo preview */}
        {msg.message_type === "image" && (() => {
          const att = msg.attachments || {};
          const fileUrl = att.file_url as string | undefined;
          return (
            <div className="space-y-1">
              {fileUrl ? (
                <img
                  src={fileUrl}
                  alt={att.filename as string || "Uploaded photo"}
                  className="max-h-48 rounded-lg border border-gray-200"
                />
              ) : (
                <p className="whitespace-pre-wrap">📷 {msg.content}</p>
              )}
            </div>
          );
        })()}

        {/* Model generating */}
        {msg.message_type === "model_generating" && (
          <div className="flex items-center gap-2 py-1">
            <div className="flex gap-1">
              <span className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <span className="text-gray-600">{msg.content}</span>
          </div>
        )}

        {/* Model preview — 3D result with action buttons */}
        {msg.message_type === "model_preview" && (() => {
          const att = msg.attachments || {};
          const previewUrl = att.preview_url as string | undefined;
          const modelRef = att.model_ref as string | undefined;
          return (
            <div className="space-y-2">
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="3D preview"
                  className="w-full rounded-lg border border-gray-200"
                />
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => modelRef && props.onDownload(modelRef, (att.download_price as number) || 10000)}
                  className="rounded-lg bg-blue-500 px-3 py-1.5 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                >
                  Download STL ₹{((att.download_price as number) || 10000) / 100}
                </button>
                <button
                  onClick={() => modelRef && props.onPrint(modelRef)}
                  className="rounded-lg border border-blue-500 px-3 py-1.5 text-blue-600 text-xs font-medium hover:bg-blue-50 transition-colors"
                >
                  Print This
                </button>
                <button
                  onClick={() => modelRef && props.onRefine(modelRef)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                >
                  Refine with Designer
                </button>
                <button
                  onClick={() => modelRef && props.onCheckPrintability(modelRef)}
                  className="rounded-lg border border-green-500 px-3 py-1.5 text-green-600 text-xs font-medium hover:bg-green-50 transition-colors"
                >
                  Check Printability
                </button>
                <button
                  onClick={() => modelRef && props.onGetQuote(modelRef)}
                  className="rounded-lg border border-blue-500 px-3 py-1.5 text-blue-600 text-xs font-medium hover:bg-blue-50 transition-colors"
                >
                  Get Print Quote
                </button>
              </div>
            </div>
          );
        })()}

        {/* Printability result (P4 bridge) */}
        {msg.message_type === "printability_result" && (() => {
          const att = msg.attachments || {};
          const status = att.status as string;
          const issues = (att.issues as string[]) || [];
          return (
            <div className="space-y-1">
              <p className={status === "PASS" ? "text-green-600 font-medium" : "text-orange-600 font-medium"}>
                {msg.content}
              </p>
              {issues.length > 0 && (
                <ul className="text-xs text-gray-600 space-y-0.5 ml-2">
                  {issues.map((iss, i) => (
                    <li key={i}>• {iss}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}

        {/* Quote result (P6 bridge) */}
        {msg.message_type === "quote_result" && (() => {
          const att = msg.attachments || {};
          const queued = att.queued as boolean;
          const quoteUrl = att.quote_url as string | undefined;
          const modelRef = att.model_ref as string | undefined;
          return (
            <div className="space-y-2">
              <p className="text-blue-600 font-medium">{msg.content}</p>
              <div className="flex gap-2">
                {quoteUrl && (
                  <a
                    href={quoteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-lg bg-blue-500 px-3 py-1.5 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                  >
                    View Quote →
                  </a>
                )}
                {modelRef && (
                  <button
                    onClick={() => props.onTrackOrder(modelRef)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                  >
                    Track Order
                  </button>
                )}
              </div>
              {queued && (
                <p className="text-xs text-gray-500">We'll send the quote to your email/phone.</p>
              )}
              {/* Sell-after-quote (customer-as-seller, 09-21): Fofy asks once,
                  right after a successful quote, for signed-in users. */}
              {fofyUser?.id && modelRef && (
                <SellAskCard projectRef={`FOF-AI-${modelRef.split(":").slice(1).join(":")}`.slice(0, 20)} quoteVisible />
              )}
            </div>
          );
        })()}

        {/* Clarify question — suggestions render as answer chips */}
        {msg.message_type === "clarify_question" && (() => {
          return (
            <div className="space-y-2">
              <p className="text-blue-600 font-medium">{msg.content}</p>
              {msg.suggestions && msg.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {msg.suggestions.map((s, j) => (
                    <button
                      key={j}
                      onClick={() => props.onSuggestion(s)}
                      className="rounded-full border border-blue-400 bg-blue-50 px-3 py-1.5 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Order status — shows handoff/quote status with link */}
        {msg.message_type === "order_status" && (() => {
          const att = msg.attachments || {};
          const quoteUrl = att.quote_url as string | undefined;
          const status = att.status as string;
          const modelRef = att.model_ref as string;
          const isSuccess = status === "succeeded";
          return (
            <div className="space-y-2">
              <p className={isSuccess ? "text-green-600 font-medium" : "text-gray-700"}>{msg.content}</p>
              <div className="flex gap-2">
                {quoteUrl && (
                  <a
                    href={quoteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-lg bg-blue-500 px-3 py-1.5 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                  >
                    View Quote →
                  </a>
                )}
                <button
                  onClick={() => modelRef && props.onTrackOrder(modelRef)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                >
                  Refresh Status
                </button>
              </div>
            </div>
          );
        })()}

        {/* Product card — credit packs, materials, designers */}
        {msg.message_type === "product_card" && (() => {
          const att = msg.attachments || {};
          const cardType = att.card_type as string;

          // Credit packs
          if (cardType === "credit_packs") {
            const packs = (att.packs as Array<{ id: number; credits: number; price_paise: number; label: string }>) || [];
            return (
              <div className="space-y-2">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {packs.map((pack) => (
                  <button
                    key={pack.id}
                    onClick={() => props.onBuyCredits(pack.id)}
                    className="flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 hover:bg-blue-100 transition-colors"
                  >
                    <div>
                      <div className="font-medium text-sm text-blue-900">{pack.label}</div>
                      <div className="text-xs text-blue-600">{pack.credits} coins</div>
                    </div>
                    <div className="font-bold text-blue-600">₹{pack.price_paise / 100}</div>
                  </button>
                ))}
              </div>
            );
          }

          // Materials
          if (cardType === "materials") {
            const materials = (att.materials as Array<{ id: string; name: string; price_per_gram: number; color: string }>) || [];
            const modelRef = att.model_ref as string;
            return (
              <div className="space-y-2">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {materials.map((mat) => (
                  <button
                    key={mat.id}
                    onClick={() => props.onManufactureOrder(modelRef, mat.id, "standard", 10)}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {mat.color && <span className="h-3 w-3 rounded-full border border-gray-300" style={{ background: mat.color }} />}
                      <span className="font-medium text-sm">{mat.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">₹{mat.price_per_gram}/g</span>
                  </button>
                ))}
              </div>
            );
          }

          // Designers
          if (cardType === "designers") {
            const designers = (att.designers as Array<{
              designer_id: string;
              name: string;
              title: string;
              rating: number;
              match_score: number;
              starting_price: string | null;
              // Skill-hourly pay model (09-16)
              hourly_rate?: number | null;
              skill_tier?: string | null;
              // Designer presence (09-16): nickname-first + online dot
              nickname?: string | null;
              is_online?: boolean;
              avatar_url: string | null;
            }>) || [];
            const modelRef = att.model_ref as string;
            return (
              <div className="space-y-2">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {designers.map((d) => (
                  <div
                    key={d.designer_id}
                    className="rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {d.avatar_url ? (
                          <img src={d.avatar_url} alt={d.name} className="h-8 w-8 rounded-full" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                            {d.name?.charAt(0) || "D"}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-1.5 font-medium text-sm">
                            {d.is_online !== false && (
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                              </span>
                            )}
                            {d.name}
                          </div>
                          <div className="text-xs text-gray-500">{d.title}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium text-green-600">{Math.round(d.match_score)}% match</div>
                        <div className="text-xs text-yellow-500">★ {d.rating?.toFixed(1) || "New"}</div>
                      </div>
                    </div>
                    {d.starting_price && (
                      <div className="mt-1 text-xs text-gray-500">From ₹{d.starting_price}</div>
                    )}
                    {typeof d.hourly_rate === "number" && (
                      <div className="mt-1 text-xs font-medium text-blue-700">
                        ₹{d.hourly_rate}/hr
                        {d.skill_tier && (
                          <span className="text-gray-400 font-normal"> · {d.skill_tier}</span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => props.onHireDesigner(d.designer_id, modelRef)}
                      className="mt-2 w-full rounded-lg bg-blue-500 px-3 py-1.5 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                    >
                      Hire
                    </button>
                  </div>
                ))}
              </div>
            );
          }

          // Uploaded 3D file — show mesh info + action buttons
          if (cardType === "uploaded_3d") {
            const modelRef = att.model_ref as string;
            const issues = (att.issues as string[]) || [];
            return (
              <div className="space-y-2">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => modelRef && props.onCheckPrintability(modelRef)}
                    className="rounded-lg border border-green-500 px-3 py-1.5 text-green-600 text-xs font-medium hover:bg-green-50 transition-colors"
                  >
                    Check Printability
                  </button>
                  <button
                    onClick={() => modelRef && props.onGetQuote(modelRef)}
                    className="rounded-lg bg-blue-500 px-3 py-1.5 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                  >
                    Get Print Quote
                  </button>
                  <button
                    onClick={() => modelRef && props.onPrint(modelRef)}
                    className="rounded-lg border border-blue-500 px-3 py-1.5 text-blue-600 text-xs font-medium hover:bg-blue-50 transition-colors"
                  >
                    Print This
                  </button>
                  <button
                    onClick={() => modelRef && props.onRefine(modelRef)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                  >
                    Refine with Designer
                  </button>
                </div>
                {issues.length > 0 && (
                  <p className="text-xs text-orange-600">
                    ⚠ Issues: {issues.join(", ")}. You can still print, but results may vary.
                  </p>
                )}
              </div>
            );
          }

          // Online 3D model search results (19 libraries) — search-first flow
          if (cardType === "online_models") {
            const models = (att.models as Array<{
              title: string;
              url: string;
              source_name: string;
              thumbnail?: string | null;
            }>) || [];
            const query = att.query as string;
            return (
              <div className="space-y-2">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <div className="space-y-1.5">
                  {models.slice(0, 6).map((m, i) => (
                    <a
                      key={`${m.url}-${i}`}
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                    >
                      {m.thumbnail ? (
                        <img src={m.thumbnail} alt="" className="h-10 w-10 shrink-0 rounded object-cover" loading="lazy" />
                      ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-blue-50 text-blue-500">
                          <Box className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900">{m.title}</div>
                        <div className="text-xs text-gray-500">{m.source_name}</div>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    </a>
                  ))}
                </div>
                <button
                  onClick={() => query && onSuggestion(`generate ${query} with AI`)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Not what I need? Generate with AI instead
                </button>
              </div>
            );
          }

          return <p className="whitespace-pre-wrap">{msg.content}</p>;
        })()}

        {/* Cost estimate */}
        {msg.message_type === "cost_estimate" && (() => {
          const att = msg.attachments || {};
          return (
            <div className="space-y-1">
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <div className="rounded-lg bg-gray-50 p-2 text-xs">
                <Row label="Filament" value={`₹${String(att.filament_cost ?? "")}`} />
                <Row label="Machine" value={`₹${String(att.machine_cost ?? "")}`} />
                <Row label="Labor" value={`₹${String(att.labor_cost ?? "")}`} />
                <div className="my-1 border-t border-gray-200" />
                <Row label="Base cost" value={`₹${String(att.base_cost ?? "")}`} />
                <div className="font-bold text-blue-600">
                  Total: ₹{String(att.selling_price ?? "")}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Order confirmation */}
        {msg.message_type === "order" && (() => {
          const att = msg.attachments || {};
          return (
            <div className="space-y-1">
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-xs">
                <div className="font-medium text-green-800">Order {String(att.order_number ?? "")}</div>
                <div className="text-green-600">Pay ₹{((att.total_paise as number) || 0) / 100} to confirm</div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-700">{value}</span>
    </div>
  );
}